import { Mail, MapPin } from "lucide-react";
import { Button } from "../../components/ui/button";
import { USER_INFO } from "../../lib/constants";
import React from "react";
import { Octokit } from "@octokit/core";

export const ContactApp: React.FC = () => {
    const [formState, setFormState] = React.useState({
      name: "",
      email: "",
      message: ""
    });
    const [errors, setErrors] = React.useState<{[key: string]: string}>({});
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [isSuccess, setIsSuccess] = React.useState(false);
    const [gitHubPage, setGitHubpage] = React.useState("");
    
    const validateForm = () => {
      const newErrors: {[key: string]: string} = {};
      
      if (!formState.name.trim()) {
        newErrors.name = "Name is required";
      }
      
      if (!formState.email.trim()) {
        newErrors.email = "Email is required";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formState.email)) {
        newErrors.email = "Please enter a valid email address";
      }
      
      if (!formState.message.trim()) {
        newErrors.message = "Message is required";
      } else if (formState.message.length < 10) {
        newErrors.message = "Message should be at least 10 characters long";
      }
      
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };
    
    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      setFormState(prev => ({
        ...prev,
        [name]: value
      }));
      
      // Clear error when user starts typing
      if (errors[name]) {
        setErrors(prev => ({
          ...prev,
          [name]: ""
        }));
      }
    };
    
    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      
      if (!validateForm()) {
        return;
      }
      
      setIsSubmitting(true);


      const octokit = new Octokit({
        auth: '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'
      })
      
      let response = await octokit.request('POST /repos/adhranjan/connection/issues', {
        owner: 'OWNER',
        repo: 'REPO',
        title: `${formState.name}`,
        body: `Email: ${formState.email} 
            message: ${formState.message} 
        `,
        assignees: [
          'octocat'
        ],
        milestone: 1,
        labels: [
          'bug'
        ],
        headers: {
          'X-GitHub-Api-Version': '2022-11-28'
        }
      });

      setGitHubpage(response.data.html_url);
      setIsSubmitting(false);
      setIsSuccess(true);
      
      // Reset form after success
      setFormState({
        name: "",
        email: "",
        message: ""
      });
        // Reset success message after 5 seconds
        setTimeout(() => {
            setIsSuccess(false);
            setGitHubpage("");
        }, 9000);
    };
    
    return (
      <div className="p-6 space-y-6">
        <div>
          <h2 className="text-lg font-semibold mb-4">Contact Information</h2>
          <div className="space-y-4">
            <div className="flex items-center">
              <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center mr-4">
                <Mail className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="font-medium">Email</div>
                <a href={`mailto:${USER_INFO.email}`} className="text-primary">{USER_INFO.email}</a>
              </div>
            </div>
            <div className="flex items-center">
              <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center mr-4">
                <MapPin className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="font-medium">Location</div>
                <div className="text-gray-600">{USER_INFO.location}</div>
              </div>
            </div>
          </div>
        </div>
        
        <div>
          <h2 className="text-lg font-semibold mb-4">Send a Message</h2>
          
          {isSuccess && (
            <div className="mb-4 p-3 bg-green-100 border border-green-200 text-green-800 rounded-md">
                Your message has been sent successfully! Please follow up the conversation  <a href={gitHubPage} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
                here
                </a>.
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formState.name}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary ${
                  errors.name ? "border-red-500" : "border-gray-300"
                }`}
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-500">{errors.name}</p>
              )}
            </div>
            
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formState.email}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary ${
                  errors.email ? "border-red-500" : "border-gray-300"
                }`}
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-500">{errors.email}</p>
              )}
            </div>
            
            <div>
              <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">Message</label>
              <textarea
                id="message"
                name="message"
                rows={4}
                value={formState.message}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary ${
                  errors.message ? "border-red-500" : "border-gray-300"
                }`}
              ></textarea>
              {errors.message && (
                <p className="mt-1 text-sm text-red-500">{errors.message}</p>
              )}
            </div>
            
            <Button 
              type="submit" 
              className="w-full relative" 
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="opacity-0">Send Message</span>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  </div>
                </>
              ) : (
                "Send Message"
              )}
            </Button>
          </form>
        </div>
      </div>
    );
  };
  